import { useBreadcrumbs } from 'global/cnr/contexts/useHelpers/useBreadcrumbs'
import { appDataSidebarHandlers, appDataSidebarInitialState, appDataSidebarReducer } from 'global/cnr/reducers/AppDataSidebarReducer'
import { createInitials } from 'global/common/dataAdjust'
import { PopupSelect } from 'global/components/popups/PopupSelect'
import SwiperWithButtons from 'global/components/swipers/SwiperWithButtons'
import ItemMenuSquares, { _imsTypes } from 'global/wrappers/ItemMenuSquares '
import _ from 'lodash'
import { useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { Dropdown, Icon } from 'semantic-ui-react'
import { FrameworkContext } from '../../cnr/contexts/FrameworkContent'
import { ParentContext } from '../../cnr/contexts/ParentContext'
import { frameworkLeftTypes } from '../../cnr/reducers/FrameworkReducer'
import { convertHelpers, getGoogleSheetDataValues } from '../../common/convert'
import { dataFix } from '../../common/dataFix'
import { getNow } from '../../common/dateFormatting'
import UiSaveButtons from '../../components/buttons/UiSaveButtons'
import DropdownOptionsMenu from '../../components/forms/elements/DropdownOptionsMenu'
import { getHeaders } from '../../components/headers/getHeaders'
import AlphabetMenuWithProvider from '../../components/menus/AlphabetMenu'
import SearchInput from '../../components/menus/SearchInput'
import TreeViewer from '../../components/viewers/TreeViewer'
import { appIconTypes } from '../../enums/appIconTypes'
import { gEnums } from '../../enums/globalEnums'
import { settingsIconTypes } from '../../enums/settingsIconTypes'
import { createRefPath_client, createRefPath_event } from '../../firestoreData/appData/appRefPaths'
import { recreateDataHelpers } from '../../firestoreData/appData/fsRecreateData'
import { updateLinkHelpers } from '../../firestoreData/updates/subUpdates/fsUpdate_dataLInks'
import ConfirmationWrapper from '../../wrappers/ConfirmationWrapper'
import Wrapper, { wrapperTypes } from '../../wrappers/Wrapper'
import { openExternal } from '../helpers/settingsLinks'
import ItemModifySidebar from './ItemModifySidebar'

const _icons = {
  list: 'list alternate outline',
  files: 'file alternate outline',
  images: 'images',
  signInAs: 'signInAs',
}

const _appDataOptionTypes = {
  backupAllData: 'backupAllData',
  backupAllDocuments: 'backupAllDocuments',
  backupData: 'backupData',
  createAppDataDocuments: 'createAppDataDocuments',
  createAppLinks: 'createAppLinks',
  createClientProfiles: 'createClientProfiles',
  createClientProfileUrls: 'createClientProfileUrls',
  createDocuments: 'createDocuments',
  exportAllToGoogleSheets: 'exportAllToGoogleSheets',
  exportToGoogleSheets: 'exportToGoogleSheets',
  fixDataLinks: 'fixDataLinks',
  mergeToCollection: 'mergeToCollection',
  removeBadData: 'removeBadData',
  restoreFromBackupData: 'restoreFromBackupData',
  viewDocumentData: 'viewDocumentData',
}

const AppDataSidebar = () => {

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, handlers, navigate, fns, fn_cls } = parentContext ?? {}
  const { app_state, appUser_state, eventInfo_state, paps_state, page_state, currentSettings_state } = states
  const { appUser } = appUser_state ?? {}
  const { appUsers_handlers, appUser_handlers, eventInfo_handlers } = handlers
  const { database_fns } = fns
  const { pathViews, rootPaths } = paps_state ?? {}
  const { appDataDocuments, appDataLinks, staticViews, eventData } = eventInfo_state ?? {}
  const { aps_global, aps_viewItems, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page } = _.get(page_state, 'pageSettings', {});
  const { viewItems } = aps_page ?? {}
  const { storageOptions, googleSheets } = aps_global ?? {}
  const { clientStorageImageCollections } = storageOptions ?? {}
  const { googleSheetsId } = googleSheets ?? {}
  const { appUserCollection, appUserViewTypeProp } = aps_appUserSettings ?? {}
  const { name: eventName } = eventData ?? {}

  const { mainAuth, mainDb, mainStorage, mainMessaging, currentEnvironment } = app_state ?? {}

  const _appDataDocuments = appDataDocuments ? appDataDocuments : staticViews

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state, framework_handlers } = frameworkContext ?? {}
  const { frInfo } = framework_state ?? {}
  const {
    currentPageData,
    view: framewordRightView,
    dataCollection: pageRightDataCollection,
    viewItem: pageRightViewItem
  } = frInfo ?? {}

  const viewItem = viewItems ? viewItems[framewordRightView] : {}

  const [appDataSidebar_state, dispatch] = useReducer(appDataSidebarReducer, appDataSidebarInitialState({ currentResults: _appDataDocuments, aps_viewItems: aps_viewItems }));
  const appDataSidebar_handlers = appDataSidebarHandlers(dispatch);

  const {
    allGroups,
    appDataKeys,
    appLinkData,
    backupData,
    clientProfiles,
    currentConfirmationType,
    currentDataLinks,
    currentResults,
    selectedAppData,
    selectedAppDataKey,
    selectedCollection,
    selectedCollectionData,
    selectedInfo,
    updating,
    useAlpha,
  } = appDataSidebar_state

  const currentGroup = allGroups?.[allGroups.length - 1] || null
  const currentIndex = allGroups.length - 1

  const [currentAlpha, setCurrentAlpha] = useState('A')

  const mergeLinks = (appDataLinks, documentLinks) => {

    const mergedLinks = _.cloneDeep(appDataLinks); // Start with a copy of appDataLinks

    _.forEach(documentLinks, (value, key) => {
      if (!_.has(appDataLinks, key)) {
        // Add missing entries from documentLinks
        mergedLinks[key] = value;
      } else if (_.isObject(value) && !_.isArray(value)) {
        // Recursively merge nested objects
        mergedLinks[key] = mergeLinks(appDataLinks[key], value);
      }
    });

    return mergedLinks;

  };

  const mergeLinksWithPrefix = (appDataLinks, documentLinks) => {
    const mergedLinks = _.cloneDeep(appDataLinks); // Start with a copy of appDataLinks

    _.forEach(documentLinks, (value, key) => {
      if (!_.has(appDataLinks, key)) {
        // Add missing entries with an '_' prefix in the key
        mergedLinks[`_${key}`] = value;
      } else if (_.isObject(value) && !_.isArray(value)) {
        // Recursively merge nested objects
        mergedLinks[key] = mergeLinksWithPrefix(appDataLinks[key], value);
      }
    });

    return mergedLinks;
  };

  useEffect(() => {
    const _appDataKeys = _appDataDocuments ? _.keys(_appDataDocuments) : []
    appDataSidebar_handlers.handleSet_appDataKeys(_.sortBy(_appDataKeys))
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [currentPageData, pageRightDataCollection, pageRightViewItem]);

  useEffect(() => {
    switch (currentConfirmationType) {
      case _appDataOptionTypes.createClientProfiles:
        const _appProfiles = {}
        _.forEach(selectedAppData, (appData, appDataKey) => {
          const { email, firstName, lastName, _itemKey } = appData
          if (email) {
            const appProfile = {
              email: email,
              firstName,
              lastName,
              displayName: firstName + ' ' + lastName,
              initials: createInitials(firstName + ' ' + lastName),
              events: {
                [pathViews.events]: {
                  name: eventName,
                  appUserKey: _itemKey
                }
              }
            }
            _appProfiles[email] = appProfile
          }
        })
        const _appProfilesS = _.orderBy(_appProfiles, ['lastName', 'firstName'])
        appDataSidebar_handlers.handleSet_clientProfiles(_appProfilesS)
        break;
      case _appDataOptionTypes.restoreFromBackupData:
        handleGetBackup()
        break;
      case _appDataOptionTypes.createDocuments:
        handleGetDocuments()
        break;
      case _appDataOptionTypes.fixDataLinks:
        handleGetDataLinks()
        break;
      case _appDataOptionTypes.createAppLinks:
        const cb = (documentLinks) => {
          const xxx = _.isEqual(documentLinks, appDataLinks)
          const getDifferences = (obj1, obj2) => {
            const changes = {};

            _.forEach(obj1, (value, key) => {
              if (!_.has(obj2, key)) {
                changes[key] = { oldValue: value, newValue: undefined };
              } else if (_.isObject(value) && !_.isArray(value)) {
                const nestedChanges = getDifferences(value, obj2[key]);
                if (!_.isEmpty(nestedChanges)) {
                  changes[key] = nestedChanges;
                }
              } else if (!_.isEqual(value, obj2[key])) {
                changes[key] = { oldValue: value, newValue: obj2[key] };
              }
            });

            _.forEach(obj2, (value, key) => {
              if (!_.has(obj1, key)) {
                changes[key] = { oldValue: undefined, newValue: value };
              }
            });

            return changes;
          };


          const differences = getDifferences(documentLinks, appDataLinks);

          const ml = mergeLinks(appDataLinks, documentLinks)
          const mlp = mergeLinksWithPrefix(appDataLinks, documentLinks)
          appDataSidebar_handlers.handleSet_appLinkData(documentLinks)
        }
        updateLinkHelpers.createAppLinks(pathViews, _appDataDocuments, cb)
      // eventInfo_handlers.handleCreate_appDataLinks(false, cb)
      default:
      // nothing
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [currentConfirmationType]);

  const handle_showConfirmation = (opts) => {
    const { optionType } = opts
    appDataSidebar_handlers.handleSet_currentConfirmationType(currentConfirmationType === optionType ? null : optionType)
  }

  const handle_viewData = (opts) => {
    console.log(appDataDocuments)
  }

  const handleOpenDb = () => {
    if (selectedInfo) {
      const { appDataItemKey } = selectedInfo
      openExternal.db(pathViews, null, [appDataItemKey])
    }
  }

  const handleShow_appDashboard = (e, opts) => {
    e.stopPropagation()
    const { frt, collectionKey } = opts ?? {}
    framework_handlers.handleShow_frameworkLeft()
    framework_handlers.handleShow_appDashboard(frt, collectionKey)
  }

  const getSelectedInfo = (key) => {
    const selected_info = { ...selectedInfo };
    const isSameSelection = key === selected_info.appDataItemKey;

    // Toggle selection state
    selected_info.appDataItemKey = isSameSelection ? null : key;

    // Update app data and view item based on new selection
    const selectedItemKey = selected_info.appDataItemKey;
    selected_info.appData = selectedItemKey ? _.cloneDeep(currentResults[selectedItemKey]) : [];
    selected_info.viewItem = aps_viewItems ? aps_viewItems[selectedItemKey] : {};
    selected_info.appDataItem = null;
    selected_info.appDataItemItem = null;

    // Extract unique properties and order app data
    const vi = aps_viewItems[selectedItemKey];
    const { dataConnection } = vi ?? {};
    const { uniqueProps } = dataConnection ?? {};

    const _selectedAppData = _.orderBy(
      selected_info.appData,
      [
        (item) => item?.selected,
        (item) => (item?.lastName || "").toLowerCase(),
        (item) => (item?.name || "").toLowerCase()
      ]
    );

    // Generate item keys and update state
    convertHelpers.createItemKeys(selected_info.appData);
    return { selected_info, _selectedAppData, uniqueProps, isSameSelection }
  }


  const handleAddDataItem = () => {
    const _selectedInfo = { ...selectedInfo }
    _selectedInfo.appDataItem = {}
    _selectedInfo.appDataItemItem = 'new'
    appDataSidebar_handlers.handleSet_selectedInfo(_selectedInfo)
  }

  const handleGotoItem = (e, key, itemKey) => {
    e && e.stopPropagation()
    let _path = rootPaths.events + key;
    if (itemKey) { _path += '/' + itemKey }
    navigate(_path)
  }

  const handleAlphaChange = (alphaItem) => {
    const _alphaChar = String.fromCharCode(alphaItem)
    setCurrentAlpha(_alphaChar)
  }

  const handleSignInAs = (e, listItem, _itemKey) => {
    e.stopPropagation()
    const actionItem = { dataActionType: 'clone', cloneId: _itemKey, cloneVit: appUserCollection, appUserViewTypeProp: appUserViewTypeProp }
    appUser_handlers.handleClone(actionItem, listItem, paps_state, appUserCollection)
    handleGotoItem(e, appUserCollection, _itemKey)
  }

  const _grouping = {
    alphaDisplayType: gEnums.alphaDisplayTypes.vertical,
    useVerticalHidden: true,
    verticalOpacity: gEnums.opacityTypes.light,
    restrictToExisting: true,
    showAlphaCount: true,
    origin: 'ads',
  }

  const alphabetMenu = (dataItems) => <AlphabetMenuWithProvider
    origin={'appDataSidebar'}
    dataItems={dataItems}
    handleAlphaChange={handleAlphaChange}
    grouping={_grouping}
  />


  const backLinks = useMemo(() => {
    if (!allGroups) return;
    const bl = _.map(
      allGroups?.slice(0, -1),
      (group, index) => (
        {
          key: group.key || 0,
          caption: _.startCase(group.key),
          item: group.item || null
        }
      )
    );
    return bl
  }, [allGroups])

  const bcrb = useBreadcrumbs({ items: backLinks, onClick: appDataSidebar_handlers.handleGo_back })

  const header_top = () => {
    const { key, text } = currentGroup
    const _caption = text || _.startCase(key)
    return <div className='swr-header'>
      {bcrb}
      <div className='swr-header-cap'><Icon color={'blue'} name='arrow left' onClick={(e) => appDataSidebar_handlers.handleGo_back()}></Icon>{_caption}</div>
    </div>
  }

  const fnAltContent = (item, itemKey) => {

    let selections = [];

    switch (currentGroup?.key) {
      case 'appData':
        selections = {
          openPage: { key: 'openPage', icon: 'file alternate outline', iconColor: 'blue' },
          // bulkImageUpload: { icon: 'images ', iconColor: 'blue' },
        }
        break;
      default:
        selections = {
          openPageItem: { icon: 'file alternate outline', iconColor: 'blue' },
        }
    }

    convertHelpers.createItemKeys(selections)

    if (_.isEmpty(selections)) { return; }

    const cb = (selectedItem) => {
      switch (selectedItem._itemKey) {
        case selections.bulkImageUpload?._itemKey:
          handleShow_appDashboard(null, { frt: frameworkLeftTypes.bulkImageUpload, collectionKey: item.key })
          break;
        case selections.openPage?._itemKey:
          handleGotoItem(null, item.key)
          break;
        case selections.openPageItem?._itemKey:
          handleGotoItem(null, currentGroup.key, item.key)
          break;
      }
    }

    console.log('selections', selections)
    return <PopupSelect items={selections} dataItem={item} handleMenuAction={cb} icon={'ellipsis vertical'} />
  }

  const filterByAlpha = (items, selectedAlpha) => {
    if (!selectedAlpha) return items;
    const alpha = _.toUpper(selectedAlpha);
    return _.filter(items, (item) => {
      const value = item.lastName || item.name || '';
      return _.startsWith(_.toUpper(value), alpha);
    });
  };

  const itemSquares_allData = () => {
    if (appDataKeys) {
      const hc = (item) => {
        // handleSelect_appData(item.key)
        appDataSidebar_handlers.handleSelect_appData(item)
      }

      const _itemz = _.map(appDataKeys, (value, key) => {
        return {
          text: _.startCase(value),
          key: value,
          count: currentResults && currentResults[value] ? Object.keys(currentResults[value]).length : 0
        }
      })

      const ims_pages = <ItemMenuSquares
        fn_altContent={fnAltContent}
        imsType={_imsTypes.inline}
        menuItems={_itemz}
        onClick={hc}
      />

      return ims_pages

    }
  }


  const itemSquares_pageData = () => {

    const hc = (item) => {
      appDataSidebar_handlers.handleSelect_appDataItem(item)
    }

    if (selectedAppData) {
      let cn = 'menu-ids ads'

      if (useAlpha) { cn += ' alpha' }

      const _itemz = []
      let currentLetter = null;

      let _selectedAppData = selectedAppData

      if (useAlpha && currentAlpha) {
        _selectedAppData = filterByAlpha(selectedAppData, currentAlpha)
      }

      _.forEach(_selectedAppData, (appData, key) => {
        const { _itemKey, name, lastName, firstName, startDate, startTime } = appData

        const displayName = lastName ? `${lastName}, ${firstName}` : name
        const alpha = displayName?.[0]?.toUpperCase()

        // 🔹 insert header when letter changes
        if (useAlpha && alpha && alpha !== currentLetter) {
          _itemz.push({
            isHeader: true,
            text: alpha,
            key: `header-${alpha}`
          })
          currentLetter = alpha
        }

        const hasSubtext = startDate
        _itemz.push({
          text: lastName ? `${lastName}, ${firstName}` : name,
          subText: hasSubtext ? `${startDate}${startTime}` : null,
          key: _itemKey,
          _itemKey: _itemKey,
        })
      })

      const ims_page = <ItemMenuSquares
        fn_altContent={fnAltContent}
        imsType={_imsTypes.inline}
        menuItems={_itemz}
        onClick={hc}
        noItemIcon={true}
      />

      const _content = useAlpha ? <div>
        {useAlpha && alphabetMenu(selectedAppData)}
        {ims_page}
      </div> : ims_page

      const ddd = <Wrapper
        content={_content}
        wrapperType={wrapperTypes.paddedHeader}
        css={{ content: useAlpha && ' alpha' }}
      />

      return ddd

    }
  }


  const header_main = () => <div className='header-flex-right'>
    <div><Icon name='arrow left' color={'blue'} onClick={(e) => { framework_handlers.handleShow_frameworkLeft() }} /></div>
    <div>{'App Data'}</div>
  </div >

  const handleSearchResults = (r) => {
    const { results } = r
    const _appDataKeys = results ? Object.keys(results) : []
    appDataSidebar_handlers.handleSet_appDataKeys(_.sortBy(_appDataKeys))
    appDataSidebar_handlers.handleSet_currentResults(results)
  }

  const searchInput = () => <SearchInput searchType={'appDataDocuments'} data={_appDataDocuments} handleResults={handleSearchResults} />

  const handleCommit = async () => {
    appDataSidebar_handlers.handleSet_updating(true)
    switch (currentConfirmationType) {
      case _appDataOptionTypes.fixDataLinks:
        const opts = { deletePrior: false, merge: true, allowUpdates: true }
        recreateDataHelpers.updateCollectionsData(pathViews, currentDataLinks, opts)
        // await handleCreateAllDocuments()
        break;
      case _appDataOptionTypes.createAppDataDocuments:
        await handleCreateAppDataDocuments()
        break;
      case _appDataOptionTypes.createDocuments:
        await handleCreateDocuments()
        break;
      case _appDataOptionTypes.exportAllToGoogleSheets:
        await handleExportAllToGoogleSheets()
        appDataSidebar_handlers.handleSet_updating(false)
        break;
      case _appDataOptionTypes.exportToGoogleSheets:
        await handleExportToGoogleSheets()
        appDataSidebar_handlers.handleSet_updating(false)
        break;
      case _appDataOptionTypes.restoreFromBackupData:
        await handleRestoreFromBackupData()
        break;
      case _appDataOptionTypes.mergeToCollection:
        await handleRemoveBadData()
        break;
      case _appDataOptionTypes.removeBadData:
        await handleRemoveBadData()
        appDataSidebar_handlers.handleSet_updating(false)
        break;
      case _appDataOptionTypes.createClientProfiles:
        await handleUpdateClientProfiles()
        break;

      case _appDataOptionTypes.createClientProfileUrls:
        await handleCreateClientProfileUrls()
        break;
      case _appDataOptionTypes.backupAllData:
        await handleBackups()
        break;
      case _appDataOptionTypes.backupAllDocuments:
        await handleBackupAllDocuments()
        break;
      case _appDataOptionTypes.backupData:
        await handleBackup()
        break;
      case _appDataOptionTypes.createAppLinks:
        await eventInfo_handlers.handleCreate_appDataLinks(true)
        appDataSidebar_handlers.handleSet_updating(false)
      case _appDataOptionTypes.viewDocumentData:
        handle_viewData()
        break;
      default:
      // nothing 
    }
  }

  const handleCreateAppDataDocuments = async () => {
    // selectedViewItemKey, commit, callback
    await eventInfo_handlers.handleCreate_appDataDocuments(null, true)
    appDataSidebar_handlers.handleSet_updating(false)
  }

  const handleCreateDocuments = async () => {
    const { appDataItemKey } = selectedInfo
    await eventInfo_handlers.handleCreate_appDataDocuments(appDataItemKey, true)
    appDataSidebar_handlers.handleSet_updating(false)
  }

  const handleGetDocuments = async () => {
    const { appDataItemKey } = selectedInfo
    const cb = (data) => {
      appDataSidebar_handlers.handleSet_selectedCollectionData(data?.[appDataItemKey])
    }
    eventInfo_handlers.handleGet_appDataDocuments(appDataItemKey, cb)
  }

  const handleGetDataLinks = () => {
    const dlks = updateLinkHelpers.enforceBidirectionalLinks(_appDataDocuments)
    appDataSidebar_handlers.handleSet_currentDataLinks(dlks)
  }

  const getGoogleSheetsData = (info) => {
    const { appDataItemKey, appData } = info
    const { headers } = getHeaders(appData)
    const { values, tabName } = getGoogleSheetDataValues(_.values(appData), headers, _.startCase(appDataItemKey), null, null, null, true, true, _appDataDocuments)
    return { values, tabName }
  }

  const handleExportAllToGoogleSheets = async () => {
    // Create an array to hold all the promises
    const promises = [];

    // Use forEach to loop through _appDataDocuments
    _.forEach(_appDataDocuments, (add, addKey) => {
      const { selected_info } = getSelectedInfo(addKey);
      const { values, tabName } = getGoogleSheetsData(selected_info);
      // Push the async call to the promises array
      const promise = fn_cls.updateGoogleSheetValues(googleSheetsId, tabName, values)
        .then(() => {
          console.log('addKey', addKey, tabName, values);
        });
      promises.push(promise);
    });

    // Wait for all promises to resolve
    await Promise.all(promises);
  }

  const handleExportToGoogleSheets = async () => {
    const { values, tabName } = getGoogleSheetsData(selectedInfo)
    await fn_cls.updateGoogleSheetValues(googleSheetsId, tabName, values)
  }

  const handleRestoreFromBackupData = async () => {

    const { appDataItemKey } = selectedInfo

    const updatePromises = _.map(backupData, (backupDataItem, key) => {
      const refPath = createRefPath_event(pathViews, [appDataItemKey, key]);
      return database_fns.doc_set(refPath, backupDataItem); // Assuming doc_update returns a promise
    });

    Promise.all(updatePromises)
      .then(() => {
        appDataSidebar_handlers.handleSet_currentConfirmationType()
        console.log(appDataItemKey + " restore completed successfully!");
      })
      .catch((error) => {
        appDataSidebar_handlers.handleSet_currentConfirmationType()
        console.error("Error during restore of " + appDataItemKey + ":", error);
      });
  }

  const handleRemoveBadData = async () => {

    const { appDataItemKey, appData } = selectedInfo

    const _keys = []
    const _items = []

    _.forEach(appData, (data, attKey) => {
      if (!data.lastName) {
        _keys.push(attKey)
        _items.push(data)
      }
    })

    await database_fns.delete_collectionKeys(pathViews, _keys, appDataItemKey)
    appDataSidebar_handlers.handleSet_currentConfirmationType()
    appDataSidebar_handlers.handleSet_updating(false)
  }

  const handleCreateClientProfileUrls = async () => {
    if (clientStorageImageCollections && clientStorageImageCollections.includes(selectedAppDataKey)) {
      const cb = (res) => {
        appDataSidebar_handlers.handleSet_updating(false)
        appDataSidebar_handlers.handleSet_currentConfirmationType()
      }
      appUsers_handlers.handleCreate_clientProfileUrls(aps_global, appUserCollection, appDataDocuments, cb)
    }
  }

  const handleBackupAllDocuments = async () => {

    const _allPromises = _.map(_appDataDocuments, (appDataDocument, dataKey) => {
      const refPath = createRefPath_event(pathViews, ['_documents', dataKey], true);
      const docsToUpdate = {}
      _.forEach(appDataDocument, (itemData, itemKey) => {
        docsToUpdate[itemKey] = dataFix.createDeleteFields(itemData)
      })
      return database_fns.doc_set(refPath, appDataDocument); // Assuming doc_update returns a promise  
    })

    const refPath_main = createRefPath_event(pathViews, [], true);
    const data_main = { lastUpdate: getNow(), uid: appUser.uid, displayName: appUser.displayName };
    _allPromises.push(database_fns.doc_set(refPath_main, data_main))

    Promise.all(_allPromises)
      .then(() => {
        appDataSidebar_handlers.handleSet_updating(false)
        appDataSidebar_handlers.handleSet_currentConfirmationType()
        console.log("Backups completed successfully!");
      })
      .catch((error) => {
        appDataSidebar_handlers.handleSet_updating(false)
        appDataSidebar_handlers.handleSet_currentConfirmationType()
        console.error("Error during backups", error);
      });
  }

  const handleUpdateClientProfiles = async () => {

    appDataSidebar_handlers.handleSet_updating(true);

    try {
      // 1. Delete all existing profiles
      const refPath1 = createRefPath_client(pathViews, ["profiles"]);
      const existingProfiles = await database_fns.get_data({ refPath: refPath1 })

      // const existingProfiles = {
      //   aaaa: {
      //     email: 'aaaa'
      //   }
      // }

      const deletePromises = _.map(existingProfiles, (existingProfile) => {
        const { email } = existingProfile;
        const refPath = createRefPath_client(pathViews, ["profiles", email]);
        return database_fns.doc_delete(refPath, null, true);
      });

      await Promise.all(deletePromises);
      console.log("Cleared profiles collection");

      // 2. Re-add updated profiles
      const addPromises = _.map(clientProfiles, (clientProfile) => {
        const { email } = clientProfile;
        const refPath = createRefPath_client(pathViews, ["profiles", email]);
        return database_fns.doc_set(refPath, clientProfile);
      });

      await Promise.all(addPromises);

      console.log("Client Profiles completed successfully!");
      appDataSidebar_handlers.handleSet_currentConfirmationType();
      appDataSidebar_handlers.handleSet_updating(false);
    } catch (error) {
      console.error("Error during Profile Update", error);
      appDataSidebar_handlers.handleSet_currentConfirmationType();
      appDataSidebar_handlers.handleSet_updating(false);
    } finally {
      appDataSidebar_handlers.handleSet_updating(false);
    }
  };

  const handleBackups = async () => {

    const _allPromises = []

    _.forEach(_appDataDocuments, (appDataDocument, dataKey) => {
      const updatePromises = _.map(appDataDocument, (appDataItem, key) => {
        const refPath = createRefPath_event(pathViews, [dataKey, key], true);
        return database_fns.doc_set(refPath, appDataItem); // Assuming doc_update returns a promise
      });
      _allPromises.push([...updatePromises])
    })

    const refPath_main = createRefPath_event(pathViews, [], true);
    const data_main = { lastUpdate: getNow(), uid: appUser.uid, displayName: appUser.displayName };
    _allPromises.push(database_fns.doc_set(refPath_main, data_main))

    Promise.all(_allPromises)
      .then(() => {
        appDataSidebar_handlers.handleSet_updating(false)
        appDataSidebar_handlers.handleSet_currentConfirmationType()
        console.log("Backups completed successfully!");
      })
      .catch((error) => {
        appDataSidebar_handlers.handleSet_updating(false)
        appDataSidebar_handlers.handleSet_currentConfirmationType()
        console.error("Error during backups", error);
      });
  }

  const handleBackup = async () => {

    const { appDataItemKey, appData } = selectedInfo
    const updatePromises = _.map(appData, (appDataItem, key) => {
      const refPath = createRefPath_event(pathViews, [appDataItemKey, key], true);
      return database_fns.doc_set(refPath, appDataItem); // Assuming doc_update returns a promise
    });

    const refPath_main = createRefPath_event(pathViews, [], true);
    const data_main = { lastUpdate: getNow(), uid: appUser.uid, displayName: appUser.displayName };
    updatePromises.push(database_fns.doc_set(refPath_main, data_main))

    Promise.all(updatePromises)
      .then(() => {
        appDataSidebar_handlers.handleSet_updating(false)
        appDataSidebar_handlers.handleSet_currentConfirmationType()
        console.log(appDataItemKey + " backup completed successfully!");
      })
      .catch((error) => {
        appDataSidebar_handlers.handleSet_updating(false)
        appDataSidebar_handlers.handleSet_currentConfirmationType()
        console.error("Error during backup of " + appDataItemKey + ":", error);
      });
  }

  const handleGetBackup = () => {
    const cb = (results) => {
      appDataSidebar_handlers.handleSet_backupData(results)
    }
    const { appDataItemKey } = selectedInfo
    const refPath = createRefPath_event(pathViews, [appDataItemKey], true);
    database_fns.get_data({ refPath, callback: cb })
  }

  const handleKeyChange = (a, b) => appDataSidebar_handlers.handleSet_selectedCollection(b?.value)

  const options = () => {
    const opts = []
    _.forEach(appDataKeys, (adk) => {
      opts.push({ text: _.startCase(adk), value: adk, key: adk })
    })
    return opts
  }

  const dropdown = () => <Dropdown
    placeholder="Select Collection"
    selection
    options={options()}
    value={selectedCollection}
    fluid
    onChange={(e, { value }) => handleKeyChange(value)}
  />

  const ddOptions_full = () => {
    const oc = handle_showConfirmation
    const btns = []
    const btnOpt = (optionType) => {
      return { caption: _.startCase(optionType), oc, icon: appIconTypes.arrowRight, fl: true, opts: { optionType } }
    }
    btns.push({ oc: handleOpenDb, caption: 'Open Database', icon: settingsIconTypes.openDatabase, fl: true, color: 'orange' })
    btns.push(btnOpt(_appDataOptionTypes.backupAllData))
    btns.push(btnOpt(_appDataOptionTypes.backupAllDocuments))
    btns.push(btnOpt(_appDataOptionTypes.createAppDataDocuments))
    btns.push(btnOpt(_appDataOptionTypes.createAppLinks))
    btns.push(btnOpt(_appDataOptionTypes.exportAllToGoogleSheets))
    btns.push(btnOpt(_appDataOptionTypes.fixDataLinks))
    btns.push(btnOpt(_appDataOptionTypes.viewDocumentData))
    return <DropdownOptionsMenu
      menuButtons={btns}
      caption={'Actions'}
      color={'blue'}
      icon={'content'}
      float={'left'}
    />
  }

  const ddOptions_dataItem = () => {
    const btns = []
    btns.push({ oc: handleOpenDb, caption: 'Open Database', icon: settingsIconTypes.openDatabase, fl: true, color: 'orange' })
    _.forEach(_appDataOptionTypes, (optionType) => {
      switch (optionType) {
        // exclude these from the main dropdown
        case _appDataOptionTypes.backupAllData:
        case _appDataOptionTypes.backupAllDocuments:
        case _appDataOptionTypes.createAppDataDocuments:
        case _appDataOptionTypes.exportAllToGoogleSheets:
        case _appDataOptionTypes.viewDocumentData:
          break;
        case _appDataOptionTypes.createClientProfiles:
        case _appDataOptionTypes.createClientProfileUrls:
          if (appUserCollection === selectedAppDataKey) {
            btns.push({ caption: _.startCase(optionType), oc: handle_showConfirmation, icon: appIconTypes.arrowRight, fl: true, opts: { optionType: optionType } })
          }
          break;
        default:
          btns.push({ caption: _.startCase(optionType), oc: handle_showConfirmation, icon: appIconTypes.arrowRight, fl: true, opts: { optionType: optionType } })
      }
    })
    return <DropdownOptionsMenu
      menuButtons={btns}
      caption={'Actions'}
      color={'blue'}
      icon={'content'}
      float={'left'}
    />
  }

  const footer_content = () => {
    const btns = []
    return <UiSaveButtons others={btns} dropdowns={ddOptions_full()} />
  }

  const footer_appDataItem = () => {
    const btns = []
    btns.push({ oc: handleAddDataItem, caption: 'Add', icon: settingsIconTypes.add, color: 'blue' })
    return <UiSaveButtons others={btns} dropdowns={ddOptions_dataItem()} />
  }

  const wrapper_confirmation = () => {

    let top;
    let content;
    let other;
    let noCommit;

    switch (currentConfirmationType) {
      case _appDataOptionTypes.createClientProfiles:
        content = <TreeViewer data={clientProfiles} subNames={true} />
        break;

      case _appDataOptionTypes.mergeToCollection:
        if (selectedAppData) {
          content = <TreeViewer data={selectedAppData} subNames={true} />
          top = dropdown()
        }
        break;

      case _appDataOptionTypes.restoreFromBackupData:
        if (backupData) {
          content = <TreeViewer data={backupData} subNames={true} />
        }
        break;
      case _appDataOptionTypes.fixDataLinks:
        if (currentDataLinks) {
          content = <TreeViewer data={currentDataLinks} subNames={true} scNames={true} />
        }
        break;
      case _appDataOptionTypes.createAppLinks:
        if (appLinkData) {
          content = <TreeViewer data={appLinkData} subNames={true} scNames={true} />
        }
        break;
      case _appDataOptionTypes.createDocuments:
        if (selectedCollectionData) {
          content = <TreeViewer data={selectedCollectionData} subNames={true} />
        }
        break;
      case _appDataOptionTypes.exportAllToGoogleSheets:
        if (selectedAppData) {
          content = <TreeViewer data={_appDataDocuments} subNames={true} scNames={true} />
          other = ' (' + googleSheetsId + ')'
        }
        break;
      case _appDataOptionTypes.exportToGoogleSheets:
        if (selectedAppData) {
          content = <TreeViewer data={selectedAppData} subNames={true} scNames={true} />
          other = ' (' + googleSheetsId + ')'
        }
        break;

      case _appDataOptionTypes.viewDocumentData:
        if (appDataDocuments) {
          content = <TreeViewer data={appDataDocuments} subNames={true} scNames={true} />
        }
        noCommit = true
        break;

      default:
    }
    return <ConfirmationWrapper
      top={top}
      showConfirmation={currentConfirmationType}
      other={other}
      handleCommit={!noCommit && handleCommit}
      handleShow={handle_showConfirmation}
      updating={updating}
      content={content}
      allowCommit={true}
    />
  }

  /**
* 
* @returns menu items of all the static views keys
*/
  const wrapper_content = () => currentConfirmationType ? wrapper_confirmation() : <Wrapper
    header={header_main()}
    search={searchInput()}
    content={itemSquares_allData()}
    footer={footer_content()}
    wrapperType={wrapperTypes.paddedHeaderAndFooter}
    updating={updating}
  />

  /**
   * 
   * @returns content of the selected static view
   */
  const wrapper_appDataItem = () => currentConfirmationType ? wrapper_confirmation() : <Wrapper
    header={header_top()}
    content={itemSquares_pageData()}
    footer={footer_appDataItem()}
    wrapperType={wrapperTypes.padded}
    css={{ container: 'app-dark-mode' }}
    updating={updating}
  />

  console.log('selectedInfo', selectedInfo)

  const wrapper_appDataItem_modify = () => <Wrapper
    header={header_top()}
    content={
      <ItemModifySidebar
        getTheData={true}
        collectionName={selectedInfo.appDataItemKey}
        itemData={selectedInfo.appDataItem}
        selectedEditItemKey={selectedInfo.appDataItemItem}
        singleDataItem={true}
        viewItem={selectedInfo.viewItem}
      />}
    wrapperType={wrapperTypes.padded}
  ></Wrapper>

  return <SwiperWithButtons
    swipeItems={[
      wrapper_content(),
      selectedInfo.appData ? wrapper_appDataItem() : <div></div>,
      selectedInfo.appDataItem ? wrapper_appDataItem_modify() : <div></div>
    ]
    }
    currentIndex={currentIndex}
    origin="AppDataSidebar"
  />
}

export default AppDataSidebar
